import React, { useState, useRef, useEffect } from "react";
import {
  Modal,
  Input,
  InputNumber,
  Button,
  List,
  Space,
  Divider,
  Typography,
  Select,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  EyeOutlined,
  SendOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import { toast } from "sonner";
import dayjs from "dayjs";

const { Title, Text } = Typography;

interface EnqueueProgressReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSend: (deadline: string, file: File) => Promise<void>;
  organization: {
    name: string;
    representativeName: string;
  };
  currentUser?: {
    name: string;
    role: string;
    company: string;
  };
  initialDeadline?: string;
}

const EnqueueProgressReportModal: React.FC<EnqueueProgressReportModalProps> = ({
  isOpen,
  onClose,
  onSend,
  organization,
  currentUser = {
    name: "Admin",
    role: "Project Manager",
    company: "25th Suite",
  },
  initialDeadline,
}) => {
  const [loading, setLoading] = useState(false);
  const [deadline, setDeadline] = useState<dayjs.Dayjs | null>(
    initialDeadline ? dayjs(initialDeadline) : null,
  );
  const [data, setData] = useState({
    clientName: organization.representativeName || "[Client Name]",
    companyName: organization.name || "[Company Name]",
    completion: 75,
    phase: "Integration",
    status: "On Track",
    systemSetup: 80,
    integration: 60,
    training: 70,
    achievements: [
      "System setup completed",
      "Integrations initiated",
      "Admin training delivered",
    ],
    risks: ["Integration delays from external systems"],
    senderName: currentUser.name,
    senderRole: currentUser.role,
    senderCompany: currentUser.company,
  });

  const previewRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (organization) {
      setData((prev) => ({
        ...prev,
        clientName: organization.representativeName || prev.clientName,
        companyName: organization.name || prev.companyName,
      }));
    }
  }, [organization]);

  useEffect(() => {
    if (initialDeadline) {
      setDeadline(dayjs(initialDeadline));
    }
  }, [initialDeadline]);

  const handleAddField = (field: "achievements" | "risks") => {
    setData((prev) => ({
      ...prev,
      [field]: [...prev[field], ""],
    }));
  };

  const handleRemoveField = (
    field: "achievements" | "risks",
    index: number,
  ) => {
    setData((prev) => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index),
    }));
  };

  const handleUpdateField = (
    field: "achievements" | "risks",
    index: number,
    value: string,
  ) => {
    const newList = [...data[field]];
    newList[index] = value;
    setData((prev) => ({
      ...prev,
      [field]: newList,
    }));
  };

  const generatePDFBlob = async (): Promise<Blob> => {
    if (!previewRef.current) throw new Error("Preview not found");

    const targetElement = previewRef.current;
    const parentElement = targetElement.parentElement;

    // Store original transform to restore after capture
    const originalTransform = parentElement
      ? parentElement.style.transform
      : "";

    // Remove transform to prevent text squishing/overlapping in html2canvas
    if (parentElement) {
      parentElement.style.transform = "none";
    }

    // Small delay to ensure browser paints/reflows before capture
    await new Promise((resolve) => setTimeout(resolve, 50));

    try {
      const canvas = await html2canvas(targetElement, {
        scale: 2, // High quality scale
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
      });

      // Compress via JPEG (quality 0.85) to significantly reduce PDF size
      const imgData = canvas.toDataURL("image/jpeg", 0.85);
      const pdf = new jsPDF("p", "mm", "a4");
      const imgProps = pdf.getImageProperties(imgData);
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

      pdf.addImage(
        imgData,
        "JPEG",
        0,
        0,
        pdfWidth,
        pdfHeight,
        undefined,
        "FAST",
      );
      return pdf.output("blob");
    } finally {
      // Restore transform
      if (parentElement) {
        parentElement.style.transform = originalTransform;
      }
    }
  };

  const handleSend = async () => {
    if (!deadline) {
      toast.error("Please select an onboarding deadline");
      return;
    }

    setLoading(true);
    try {
      const blob = await generatePDFBlob();
      const file = new File(
        [blob],
        `Progress_Report_${organization.name.replace(/\s+/g, "_")}.pdf`,
        {
          type: "application/pdf",
        },
      );
      await onSend(deadline.toISOString(), file);
      onClose();
    } catch (error) {
      console.error("Failed to generate or send PDF", error);
      toast.error("Failed to generate or send progress report");
    } finally {
      setLoading(false);
    }
  };

  const renderTemplate = () => (
    <div
      ref={previewRef}
      style={{
        width: "620px",
        margin: "0 auto",
        background: "#ffffff",
        borderRadius: "12px",
        overflow: "hidden",
        boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
        fontFamily:
          "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif",
        color: "#344054",
        textAlign: "left",
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: "24px 30px",
          borderBottom: "1px solid #eef1f6",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div>
          <strong style={{ fontSize: "18px", color: "#111" }}>
            {data.senderCompany}
          </strong>
          <br />
          <span style={{ fontSize: "13px", color: "#667085" }}>
            {data.companyName} Implementation
          </span>
        </div>
        <div
          style={{
            background: "#ecfdf3",
            color: "#027a48",
            padding: "6px 10px",
            borderRadius: "20px",
            fontSize: "12px",
          }}
        >
          ● {data.status}
        </div>
      </div>

      {/* Intro */}
      <div style={{ padding: "30px" }}>
        <p style={{ margin: "0 0 10px", fontSize: "15px" }}>
          Dear {data.clientName},
        </p>
        <p style={{ margin: 0, fontSize: "15px", color: "#475467" }}>
          Here’s your latest progress update for the{" "}
          <strong>{data.senderCompany} implementation</strong>.
        </p>
      </div>

      {/* KPI Cards */}
      <div style={{ padding: "0 30px 20px", display: "flex", gap: "10px" }}>
        <div
          style={{
            flex: 1,
            background: "#f9fafb",
            border: "1px solid #eef1f6",
            borderRadius: "10px",
            padding: "15px",
          }}
        >
          <div style={{ fontSize: "12px", color: "#667085" }}>Completion</div>
          <div style={{ fontSize: "22px", fontWeight: "bold", color: "#111" }}>
            {data.completion}%
          </div>
        </div>
        <div
          style={{
            flex: 1,
            background: "#f9fafb",
            border: "1px solid #eef1f6",
            borderRadius: "10px",
            padding: "15px",
          }}
        >
          <div style={{ fontSize: "12px", color: "#667085" }}>Phase</div>
          <div style={{ fontSize: "16px", fontWeight: "bold", color: "#111" }}>
            {data.phase}
          </div>
        </div>
        <div
          style={{
            flex: 1,
            background: "#f9fafb",
            border: "1px solid #eef1f6",
            borderRadius: "10px",
            padding: "15px",
          }}
        >
          <div style={{ fontSize: "12px", color: "#667085" }}>Go-Live</div>
          <div
            style={{ fontSize: "16px", fontWeight: "bold", color: "#027a48" }}
          >
            {data.status}
          </div>
        </div>
      </div>

      {/* Progress Section */}
      <div style={{ padding: "0 30px 20px" }}>
        <h3 style={{ margin: "0 0 15px", fontSize: "16px", color: "#101828" }}>
          Progress Overview
        </h3>

        <div style={{ marginBottom: "15px" }}>
          <p style={{ margin: "0 0 5px", fontSize: "13px", color: "#475467" }}>
            System Setup ({data.systemSetup}%)
          </p>
          <div
            style={{
              background: "#eef2f7",
              borderRadius: "6px",
              height: "8px",
              width: "100%",
            }}
          >
            <div
              style={{
                width: `${data.systemSetup}%`,
                background: "#3b82f6",
                height: "100%",
                borderRadius: "6px",
              }}
            ></div>
          </div>
        </div>

        <div style={{ marginBottom: "15px" }}>
          <p style={{ margin: "0 0 5px", fontSize: "13px", color: "#475467" }}>
            Integration ({data.integration}%)
          </p>
          <div
            style={{
              background: "#eef2f7",
              borderRadius: "6px",
              height: "8px",
              width: "100%",
            }}
          >
            <div
              style={{
                width: `${data.integration}%`,
                background: "#3b82f6",
                height: "100%",
                borderRadius: "6px",
              }}
            ></div>
          </div>
        </div>

        <div>
          <p style={{ margin: "0 0 5px", fontSize: "13px", color: "#475467" }}>
            Training ({data.training}%)
          </p>
          <div
            style={{
              background: "#eef2f7",
              borderRadius: "6px",
              height: "8px",
              width: "100%",
            }}
          >
            <div
              style={{
                width: `${data.training}%`,
                background: "#3b82f6",
                height: "100%",
                borderRadius: "6px",
              }}
            ></div>
          </div>
        </div>
      </div>

      {/* Achievements & Risks */}
      <div style={{ padding: "0 30px 30px", display: "flex", gap: "20px" }}>
        <div
          style={{
            flex: 1,
            background: "#f0fdf4",
            border: "1px solid #dcfce7",
            borderRadius: "10px",
            padding: "15px",
          }}
        >
          <strong style={{ color: "#166534" }}>Key Achievements</strong>
          <ul
            style={{
              paddingLeft: "18px",
              margin: "10px 0",
              color: "#14532d",
              fontSize: "13px",
            }}
          >
            {data.achievements.map(
              (item, i) => item && <li key={i}>{item}</li>,
            )}
          </ul>
        </div>
        <div
          style={{
            flex: 1,
            background: "#fffbeb",
            border: "1px solid #fef3c7",
            borderRadius: "10px",
            padding: "15px",
          }}
        >
          <strong style={{ color: "#92400e" }}>Risks</strong>
          <ul
            style={{
              paddingLeft: "18px",
              margin: "10px 0",
              color: "#78350f",
              fontSize: "13px",
            }}
          >
            {data.risks.map((item, i) => item && <li key={i}>{item}</li>)}
          </ul>
        </div>
      </div>

      {/* Footer */}
      <div
        style={{
          padding: "20px 30px",
          borderTop: "1px solid #eef1f6",
          fontSize: "13px",
          color: "#667085",
        }}
      >
        We’ll continue to keep you updated as we approach go-live.
        <br />
        <br />
        <strong>{data.senderName}</strong>
        <br />
        {data.senderRole}
        <br />
        {data.senderCompany}
      </div>
    </div>
  );

  return (
    <Modal
      title={
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <SendOutlined />
          <span>Schedule Progress Report Template</span>
        </div>
      }
      open={isOpen}
      onCancel={onClose}
      width={1200}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="send"
          type="primary"
          icon={<SendOutlined />}
          loading={loading}
          onClick={handleSend}
        >
          Generate & Schedule Report
        </Button>,
      ]}
    >
      <div style={{ display: "flex", gap: "10px", height: "80vh" }}>
        {/* Left: Editor */}
        <div style={{ flex: 1, overflowY: "auto", paddingRight: "10px" }}>
          <Space direction="vertical" style={{ width: "100%" }} size="large">
            <div>
              <Title level={5}>Onboarding Information</Title>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "10px",
                }}
              >
                <div>
                  <Text type="secondary">Onboarding Deadline</Text>
                  <Select
                    style={{ width: "100%" }}
                    value={deadline ? deadline.diff(dayjs(), 'day') > 0 ? deadline.diff(dayjs(), 'day') : undefined : undefined}
                    onChange={(val) => setDeadline(dayjs().add(val as number, 'day'))}
                    placeholder="Select deadline"
                    options={[
                      { value: 30, label: "30 Days" },
                      { value: 60, label: "60 Days" },
                      { value: 90, label: "90 Days" },
                      { value: 120, label: "120 Days" },
                    ]}
                  />
                </div>
                <div>
                  <Text type="secondary">Client Name</Text>
                  <Input
                    value={data.clientName}
                    onChange={(e) =>
                      setData({ ...data, clientName: e.target.value })
                    }
                  />
                </div>
                <div>
                  <Text type="secondary">Phase</Text>
                  <Input
                    value={data.phase}
                    onChange={(e) =>
                      setData({ ...data, phase: e.target.value })
                    }
                  />
                </div>
                <div>
                  <Text type="secondary">Completion % (Overall)</Text>
                  <InputNumber
                    style={{ width: "100%" }}
                    min={0}
                    max={100}
                    value={data.completion}
                    onChange={(val) =>
                      setData({ ...data, completion: val || 0 })
                    }
                  />
                </div>
                <div>
                  <Text type="secondary">Status</Text>
                  <Input
                    value={data.status}
                    onChange={(e) =>
                      setData({ ...data, status: e.target.value })
                    }
                  />
                </div>
              </div>
            </div>

            <Divider style={{ margin: "10px 0" }} />

            <div>
              <Title level={5}>Progress Metrics (%)</Title>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr",
                  gap: "10px",
                }}
              >
                <div>
                  <Text type="secondary">System Setup</Text>
                  <InputNumber
                    style={{ width: "100%" }}
                    min={0}
                    max={100}
                    value={data.systemSetup}
                    onChange={(val) =>
                      setData({ ...data, systemSetup: val || 0 })
                    }
                  />
                </div>
                <div>
                  <Text type="secondary">Integration</Text>
                  <InputNumber
                    style={{ width: "100%" }}
                    min={0}
                    max={100}
                    value={data.integration}
                    onChange={(val) =>
                      setData({ ...data, integration: val || 0 })
                    }
                  />
                </div>
                <div>
                  <Text type="secondary">Training</Text>
                  <InputNumber
                    style={{ width: "100%" }}
                    min={0}
                    max={100}
                    value={data.training}
                    onChange={(val) => setData({ ...data, training: val || 0 })}
                  />
                </div>
              </div>
            </div>

            <Divider style={{ margin: "10px 0" }} />

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "20px",
              }}
            >
              <div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "8px",
                  }}
                >
                  <Title level={5} style={{ margin: 0 }}>
                    Achievements
                  </Title>
                  <Button
                    size="small"
                    icon={<PlusOutlined />}
                    onClick={() => handleAddField("achievements")}
                  />
                </div>
                <List
                  dataSource={data.achievements}
                  renderItem={(item, index) => (
                    <List.Item style={{ padding: "4px 0", border: "none" }}>
                      <Input
                        value={item}
                        placeholder="Achievement description"
                        onChange={(e) =>
                          handleUpdateField(
                            "achievements",
                            index,
                            e.target.value,
                          )
                        }
                        suffix={
                          <DeleteOutlined
                            style={{ color: "#ff4d4f" }}
                            onClick={() =>
                              handleRemoveField("achievements", index)
                            }
                          />
                        }
                      />
                    </List.Item>
                  )}
                />
              </div>

              <div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "8px",
                  }}
                >
                  <Title level={5} style={{ margin: 0 }}>
                    Risks
                  </Title>
                  <Button
                    size="small"
                    icon={<PlusOutlined />}
                    onClick={() => handleAddField("risks")}
                  />
                </div>
                <List
                  dataSource={data.risks}
                  renderItem={(item, index) => (
                    <List.Item style={{ padding: "4px 0", border: "none" }}>
                      <Input
                        value={item}
                        placeholder="Risk description"
                        onChange={(e) =>
                          handleUpdateField("risks", index, e.target.value)
                        }
                        suffix={
                          <DeleteOutlined
                            style={{ color: "#ff4d4f" }}
                            onClick={() => handleRemoveField("risks", index)}
                          />
                        }
                      />
                    </List.Item>
                  )}
                />
              </div>
            </div>

            <Divider style={{ margin: "10px 0" }} />

            <div>
              <Title level={5}>Sender Information</Title>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr",
                  gap: "10px",
                }}
              >
                <div>
                  <Text type="secondary">Name</Text>
                  <Input
                    value={data.senderName}
                    onChange={(e) =>
                      setData({ ...data, senderName: e.target.value })
                    }
                  />
                </div>
                <div>
                  <Text type="secondary">Role</Text>
                  <Input
                    value={data.senderRole}
                    onChange={(e) =>
                      setData({ ...data, senderRole: e.target.value })
                    }
                  />
                </div>
                <div>
                  <Text type="secondary">Company</Text>
                  <Input
                    value={data.senderCompany}
                    onChange={(e) =>
                      setData({ ...data, senderCompany: e.target.value })
                    }
                  />
                </div>
              </div>
            </div>
          </Space>
        </div>

        {/* Right: Preview */}
        <div
          style={{
            flex: 1,
            background: "#f0f2f5",
            padding: "20px",
            borderRadius: "8px",
            overflowY: "auto",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "center",
          }}
        >
          <div>
            <div style={{ marginBottom: "10px", textAlign: "right" }}>
              <Text type="secondary">
                <EyeOutlined /> Live Preview
              </Text>
            </div>
            <div
              style={{
                transform: "scale(0.85)",
                transformOrigin: "top center",
              }}
            >
              {renderTemplate()}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default EnqueueProgressReportModal;
